// src/components/CancelSubscriptionModal.js
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Alert,
  Box,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch
} from '@mui/material';
import {
  Warning as WarningIcon,
  Block as BlockIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const CANCELLATION_REASONS = [
  { value: 'too_expensive', label: 'Too expensive' },
  { value: 'not_using_enough', label: 'Not using enough features' },
  { value: 'missing_features', label: 'Missing features I need' },
  { value: 'technical_issues', label: 'Technical issues or bugs' },
  { value: 'switching_competitor', label: 'Switching to a competitor' },
  { value: 'business_closing', label: 'Business closing or changing' },
  { value: 'other', label: 'Other reason' }
];

const CancelSubscriptionModal = ({ open, onClose, subscription, onCancel }) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [feedback, setFeedback] = useState('');
  const [cancelImmediately, setCancelImmediately] = useState(false); // Always false - cancel at period end
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [error, setError] = useState(null);

  const handleReasonChange = (event) => {
    setSelectedReason(event.target.value);
  };

  const handleProceedToConfirmation = () => {
    if (!selectedReason) {
      setError('Please select a cancellation reason');
      return;
    }
    setError(null);
    setShowConfirmation(true);
  };

  const handleBackToReasons = () => {
    setError(null);
    setShowConfirmation(false);
  };

  const handleConfirmCancellation = async () => {
    if (!selectedReason) {
      setError('Please select a cancellation reason');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    
    try {
      await onCancel(selectedReason, feedback, cancelImmediately);
      handleClose();
    } catch (error) {
      console.error('Cancellation error:', error);
      
      // Show error in modal instead of just relying on parent
      let errorMessage = 'Failed to cancel subscription';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedReason('');
    setFeedback('');
    setError(null);
    // cancelImmediately is always false, no need to reset
    setShowConfirmation(false);
    setIsSubmitting(false);
    onClose();
  };

  const getConsequences = () => {
    const consequences = [];
    
    // Always show period-end consequences (removed immediate cancellation option)
    consequences.push({
      icon: <ScheduleIcon color="warning" />,
      text: `Access continues until ${subscription?.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : 'end of billing period'}`
    });
    consequences.push({
      icon: <BlockIcon color="warning" />,
      text: 'No automatic renewal - subscription ends at period end'
    });

    consequences.push({
      icon: <InfoIcon color="info" />,
      text: 'Data will be preserved for 30 days after cancellation'
    });

    consequences.push({
      icon: <InfoIcon color="info" />,
      text: 'You can reactivate within 30 days with all your data intact'
    });

    return consequences;
  };

  if (!subscription) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      {!showConfirmation ? (
        <>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon color="warning" />
              <Typography variant="h6">
                Cancel Subscription
              </Typography>
            </Box>
          </DialogTitle>
          
          <DialogContent>
            <Alert severity="info" sx={{ mb: 3 }}>
              We're sorry to see you go! Help us understand why you're canceling 
              so we can improve MedInsight Pro for everyone.
            </Alert>

            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              What's your main reason for canceling?
            </Typography>

            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={selectedReason}
                onChange={handleReasonChange}
              >
                {CANCELLATION_REASONS.map((reason) => (
                  <FormControlLabel
                    key={reason.value}
                    value={reason.value}
                    control={<Radio />}
                    label={reason.label}
                    sx={{ mb: 1 }}
                  />
                ))}
              </RadioGroup>
            </FormControl>

            {selectedReason === 'other' && (
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Please tell us more"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                sx={{ mt: 2 }}
                placeholder="Help us understand your specific situation..."
              />
            )}

            {selectedReason && selectedReason !== 'other' && (
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Additional feedback (optional)"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                sx={{ mt: 2 }}
                placeholder="Any additional details that might help us improve..."
              />
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleClose} color="inherit">
              Keep Subscription
            </Button>
            <Button
              onClick={handleProceedToConfirmation}
              variant="contained"
              color="warning"
              disabled={!selectedReason}
            >
              Continue Cancellation
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon color="error" />
              <Typography variant="h6">
                Confirm Cancellation
              </Typography>
            </Box>
          </DialogTitle>
          
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Are you sure you want to cancel your subscription?
              </Typography>
            </Alert>

            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Current Plan: {subscription.plan_name || 'Professional'}
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Your subscription will be cancelled at the end of your current billing period.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                You'll continue to have full access until {subscription?.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : 'the end of your billing cycle'}.
              </Typography>
            </Alert>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              What happens when you cancel:
            </Typography>

            <List dense>
              {getConsequences().map((consequence, index) => (
                <ListItem key={index}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {consequence.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={consequence.text}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>

            {selectedReason && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Cancellation reason: {CANCELLATION_REASONS.find(r => r.value === selectedReason)?.label}
                </Typography>
                {feedback && (
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                    Feedback: {feedback}
                  </Typography>
                )}
              </Box>
            )}
            
            {/* Error display */}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleBackToReasons} color="inherit">
              Back
            </Button>
            <Button onClick={handleClose} color="inherit">
              Keep Subscription
            </Button>
            <Button
              onClick={handleConfirmCancellation}
              variant="contained"
              color="error"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Canceling...' : 'Confirm Cancellation'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default CancelSubscriptionModal;